import Endpoints from "./endpoints.js";
import Agente from "./models/Agente";
import CryptoJS from "crypto-js";
import fetchWithAuth from "./Utilities/fetchWithAuth";
import { handleCredentialError } from "./Utilities/authGuard";

const sanitizeEmail = (email) =>
  (email || "")
    .toString()
    .trim()
    // .toLowerCase();

const normalizeEmailWithSpaces = (email) =>
  sanitizeEmail(email).replace(/\./g, " ").replace(/\s+/g, " ").trim();

const normalizeEmailComparable = (email) =>
  normalizeEmailWithSpaces(email).replace(/\s+/g, "");

const toDotEmail = (email) => normalizeEmailWithSpaces(email).replace(/ /g, ".");

const getEmailVariants = (email) => {
  const withSpaces = normalizeEmailWithSpaces(email);
  const withDots = toDotEmail(email);
  return Array.from(new Set([withSpaces, withDots].filter(Boolean)));
};

const getCurrentAgentEmailVariants = (currentAgent) => {
  const rawEmails = [
    currentAgent?.email,
    currentAgent?.usuario?.emailreal,
    currentAgent?.usuario?.email,
    currentAgent?.usuario?.grupo,
  ].filter(Boolean);

  return Array.from(new Set(rawEmails.flatMap((email) => getEmailVariants(email))));
};

const buildContactFromDocument = (document) => {
  const brAgentSpaces = normalizeEmailWithSpaces(document.BrAgent);
  const ciphertext = CryptoJS.AES.encrypt(
    document.WaId,
    "1F04iqoixAOvibnsDRt",
  ).toString();
  const waIdHash = CryptoJS.enc.Base64.stringify(
    CryptoJS.enc.Utf8.parse(ciphertext),
  );

  return {
    id: document.WaId,
    idhash: waIdHash,
    name: document.Nombre,
    number: document.WaId,
    pic: document.ImagenPerfil,
    lastSeen: document.Timestamp * 1000,
    br_agent: document.BrAgent,
    br_agent_spaces: brAgentSpaces,
    br_agent_dots: toDotEmail(document.BrAgent),
    br_agent_normalized: normalizeEmailComparable(document.BrAgent),
    lastMessage: document.LastMessage,
    Unread: document.hasOwnProperty("Unread") ? document.Unread : false,
    ExtraFields: document.hasOwnProperty("CamposExtra")
      ? document.CamposExtra
      : null,
    Campaign: document.hasOwnProperty("Campaign") ? document.Campaign : null,
    hasError: document.hasOwnProperty("hasError") ? document.hasError : false,
  };
};

const getMessageFallbackKey = (messageLike, fallbackPrefix = "msg") => {
  if (messageLike?.key) {
    return String(messageLike.key);
  }

  const time = Number(messageLike?.Timestamp || messageLike?.time || 0);
  const waid = String(messageLike?.WaId || messageLike?.sender || messageLike?.recvId || "");
  const body = String(messageLike?.Mensaje || messageLike?.Message || messageLike?.body || "");
  const status = String(messageLike?.Status || messageLike?.status || "");
  const type = String(messageLike?.Tipo || messageLike?.messagetype || "");

  return `${fallbackPrefix}-${waid}-${time}-${status}-${type}-${body}`;
};
/**
 *
 * @param context
 * @param {Agente} context.state.currentAgent
 * @returns {Promise<any>|{loadBroadcastLists: (function(*=): Promise<any>), createBroadcastList: (function(*=, *=): Promise<any>), loadArchive(*=): Promise<*>, loadFullDirectory: (function(*=): Promise<any>), loadTemplates: loadTemplates, appendBroadcastListMembers: (function(*=, *, *=): Promise<any>), removeBroadcastListMember: (function(*=, *, *): Promise<Response>), updateMessages: updateMessages, loadUsers: loadUsers, loadQuickReplies: loadQuickReplies, loadTypification: loadTypification, loadContacts: loadContacts, loadMessages: loadMessages, removeBroadcastList: (function(*=, *): Promise<Response>), removeContacts: removeContacts, updateContacts: updateContacts, loadBroadcastListMembers: (function(*=, *): Promise<any>), updateUsers: updateUsers}}
 * @constructor
 */
export default function State(context) {
  const tipo = "cartera-clientes";
  const handleFirebaseCredentialError = (error) => {
    if (!handleCredentialError(error)) {
      console.error("Firebase query error", error)
    }
  }

  return {
    loadArchive(waid) {
      return fetchWithAuth(Endpoints().GCP_UNARCHIVE_MESSAGES_URL, {
        body: JSON.stringify({ WaId: waid }),
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " + context.state.currentAgent.stsTokenManager.accessToken,
        },
        method: "POST",
      })
        .then((response) => response.json())
        .then((collection) => {
          const messages = collection.Results.map((msg) => {
            const normalizedTimestamp = Number(msg.Timestamp) * 1000;
            const message = {
              sender: msg.Type === 0 ? waid : 0,
              body: msg.Message, //.replace(/~(.*)~/g, '<del>$1</del>').replace(/_(.*)_/g, '<em>$1</em>').replace(/\*(.*)\*/g, '<strong>$1</strong>'),
              time: normalizedTimestamp,
              status: msg.Status,
              recvId: msg.Type === 1 || msg.Type === 3 ? waid : 0,
              brAgent: msg.BrAgent,
              recvIsGroup: false,
              key: msg.Key || getMessageFallbackKey(msg, "archive"),
              messagetype: msg.Type,
            };
            if (msg.TypeObject != null && msg.TypeObject !== "") {
              message.typeObject = msg.TypeObject;
            }
            if (msg.ObjectUrl != null && msg.ObjectUrl !== "") {
              message.objectUrl = msg.ObjectUrl;
            }
            return message;
          });
          return messages;
          // that.setState({ messageList: newState });
        })
        .catch((error) => {
          console.log("Error Action loadArchive", error);
          context.setState({ requireLogOut: true });
        });
    },

    loadContacts: (db) => {
      const contactsBuffer = [];
      let isInitialLoad = true;
      const queryValues = getCurrentAgentEmailVariants(context.state.currentAgent);
      if (queryValues.length === 0) {
        context.setState({ contactList: [], appIsLoading: false });
        return;
      }

      const upsertInBuffer = (contact) => {
        const existingIndex = contactsBuffer.findIndex((c) => c.id === contact.id);
        if (existingIndex === -1) {
          contactsBuffer.push(contact);
          return;
        }
        contactsBuffer[existingIndex] = {
          ...contactsBuffer[existingIndex],
          ...contact,
        };
      };

      let pendingInitialLoads = queryValues.length;

      queryValues.forEach((queryValue) => {
        let query = db
          .ref("contacto")
          .orderByChild("BrAgent")
          .equalTo(queryValue);

        query.on("child_added", (data) => {
          const document = data.val();

          if (
            !document ||
            !document.hasOwnProperty("LastMessage") ||
            !document.LastMessage.hasOwnProperty("Inbound") ||
            !document.LastMessage.hasOwnProperty("Outbound")
          ) {
            return;
          }

          const contact = buildContactFromDocument(document);
          upsertInBuffer(contact);

          if (!isInitialLoad) {
            const contactsBuffer2 = [...context.state.contactList];
            const existingIndex = contactsBuffer2.findIndex((c) => c.id === contact.id);
            if (existingIndex === -1) {
              contactsBuffer2.push(contact);
            } else {
              contactsBuffer2[existingIndex] = {
                ...contactsBuffer2[existingIndex],
                ...contact,
              };
            }
            context.setState({ contactList: contactsBuffer2 });
          }
        }, handleFirebaseCredentialError);

        query
          .once("value")
          .then(() => {
            pendingInitialLoads -= 1;
            if (pendingInitialLoads === 0) {
              context.setState({ contactList: contactsBuffer, appIsLoading: false });
              isInitialLoad = false;
            }
          })
          .catch(handleFirebaseCredentialError);
      });
    },
    addTemplate: (db, id, template) => {
      if (id) {
        const objectRef = db.ref("templates");
        objectRef.child(id).set(template);
      }
    },
    deleteTemplate: (db, id) => {
      if (id) {
        const objectRef = db.ref("templates");
        objectRef.child(id).remove();
      }
    },
    /*
        loadMessages: (db) => {
            let messagesBuffer = []
            let isInitialLoad = true;
            db.ref('mensaje').on('child_added', function(data){
              let document = data.val();
              let key = data.key;

              let message = {
                  sender: document.Tipo === 0 ? document.WaId : 0,
                  body: document.Mensaje,
                  time: (document.Timestamp *  1000),
                  status: document.Status,
                  recvId: document.Tipo === 1 ? document.WaId : 0,
                  brAgent: document.BrAgent ? document.BrAgent.replace(/ /g, "."): '',
                  recvIsGroup: false,
                  key: key
              };

              // if(context.state.currentConversation === message.sender){
              //   message.status=2;
              //   let msgF =db.ref('mensaje/');
              //   msgF.child(message.key).update({'Status':2});
              // }

              messagesBuffer.push(message)
              if(!isInitialLoad) {
                  context.setState({ messageList: messagesBuffer})
              }
            })

            setTimeout(()=> {
                context.setState({ messageList: messagesBuffer })
                isInitialLoad = false;
                context.setState({ appIsLoading: false })
            }, 15000)
        },
*/

    loadMessages(db, waid, lastTimestamp = 0) {
      const contactListListenerB = context.state.contactListListener;
      if (!contactListListenerB.find((element) => element === waid)) {
        contactListListenerB.push(waid);
        context.setState({ contactListListener: contactListListenerB });

        let refdb = db.ref("conversacion/" + waid);
        
        // Simple listener without filters - we'll filter in the callback
        refdb.on("child_added", (data) => {
          let document = data.val();
          let key = data.key;

          if (document.Mensaje != null) {
            const normalizedTimestamp = Number(document.Timestamp) * 1000;

            let message = {
              sender: document.Tipo === 0 ? document.WaId : 0,
              body: document.Mensaje,
              time: normalizedTimestamp,
              status: document.Status,
              recvId:
                document.Tipo === 1 ||
                document.messagetype === 3 ||
                document.Tipo === 3
                  ? document.WaId
                  : 0,
              brAgent: document.BrAgent
                ? document.BrAgent.replace(/ /g, ".")
                : "",
              recvIsGroup: false,
              key: key || getMessageFallbackKey(document, "firebase"),
              messagetype: document.messagetype
                ? document.messagetype
                : document.Tipo,
            };
            if (document.TypeObject != null && document.TypeObject !== "") {
              message.typeObject = document.TypeObject;
            }
            if (document.ObjectUrl != null && document.ObjectUrl !== "") {
              message.objectUrl = document.ObjectUrl;
            }
            
            // Filter: only add messages AFTER lastTimestamp or if it's the initial load (lastTimestamp=0)
            const messageTime = normalizedTimestamp; // Convert to ms
            
            if (!Number.isFinite(messageTime)) {
              return;
            }

            if (messageTime > Number(lastTimestamp || 0)) {
              // Only add messages that belong to the current conversation
              if (message.sender === waid || message.recvId === waid) {
                const currentMessages = context.state.messageList;
                
                const messageExists = currentMessages.some((msg) => msg.key === key);
                
                if (!messageExists) {
                  const updatedMessages = [...currentMessages, message];
                  context.setState({ messageList: updatedMessages });
                }
              }
            }
          }
        }, handleFirebaseCredentialError);
      }
    },
    loadTemplates: (db) => {
      db.ref("templates").on("child_added", (data) => {
        const document = data.val();
        const key = data.key;

        document.id = key;

        const newState = context.state.templateList.concat(document);

        context.setState({ templateList: newState });
      }, handleFirebaseCredentialError);
    },
    /*
        updateMessages: (db) => {
            db.ref('mensaje').on('child_changed', function(data) {
                let key = data.key;
                let document = data.val()

                let newState = context.state.messageList.map(msg => {
                    if(msg.key === key) {
                        msg.status = document.Status;
                        msg.body= document.Mensaje;
                        return msg
                    }
                    return msg;
                })

                context.setState( { messageList: newState })
            });
        },
*/
    updateMessages: (db, waid) => {
      db.ref("conversacion/" + waid).on("child_changed", (data) => {
        const key = data.key;
        const document = data.val();

        // Only update messages that belong to the current conversation
        const newState = context.state.messageList.map((msg) => {
          if (msg.key === key && (msg.sender === waid || msg.recvId === waid)) {
            const updatedMsg = { ...msg };
            
            if (document.hasOwnProperty("Status")) {
              updatedMsg.status = document.Status;
            }
            if (document.hasOwnProperty("Mensaje")) {
              updatedMsg.body = document.Mensaje;
            }

            return updatedMsg;
          }
          return msg;
        });

        context.setState({ messageList: newState });
      }, handleFirebaseCredentialError);
    },
    updateContacts: (db) => {
      let query = db.ref("contacto");
      query.on("child_changed", (data) => {
        const key = data.key;
        const document = data.val();
        const currentConversation = context.state.currentConversation;
        const currentAgentVariants = getCurrentAgentEmailVariants(
          context.state.currentAgent,
        );
        const brAgentMatchesCurrentAgent = String(document.BrAgent || "")
          .split(",")
          .map((token) => normalizeEmailComparable(token))
          .filter(Boolean)
          .some((token) =>
            currentAgentVariants.some(
              (variant) => normalizeEmailComparable(variant) === token,
            ),
          );
        // let findConctact = false;
        // console.log('actualizada contacto:');
        // console.log(document);
        const newState = context.state.contactList.map((contact) => {
          if (contact.id === key) {
            // findConctact=true;
            // console.log('Encontro en la lista');

            let updatedUnread = document.Unread;

            if (key === currentConversation) {
              updatedUnread = false;
            }

            // New inbound message
            if (
              contact.lastMessage.Inbound.ID !==
                document.LastMessage.Inbound.ID &&
              brAgentMatchesCurrentAgent
            ) {
              if (key === currentConversation) {
                updatedUnread = false;
              } else {
                updatedUnread = true;
                context.updateContactCallback(
                  key,
                  "Nuevo mensaje",
                  document.LastMessage.Inbound.Text,
                );
              }
            }
            // or conversation transfer
            if (
              contact.br_agent !== document.BrAgent &&
              document.BrAgent ===
                context.state.currentAgent.usuario.emailreal.replace(/\./g, " ")
            ) {
              context.updateContactCallback(
                key,
                "Asignación de conversación",
                `Asigna: ${contact.br_agent}`,
              );
            }

            // Update all contact properties
            const updatedContact = {
              ...contact,
              br_agent: document.BrAgent,
              lastMessage: document.LastMessage,
              Unread: updatedUnread,
              name: document.Nombre,
              Campaign: document.Campaign,
              hasError: document.hasOwnProperty("hasError")
                ? document.hasError
                : false,
            };

            return updatedContact;
          }
          return contact;
        });
        // console.log('Encontro en la lista: ');
        // console.log(findConctact);
        // if(!findConctact){
        //     const contact  = {
        //         id: document.WaId,
        //         name: document.Nombre,
        //         number: document.WaId,
        //         pic: document.ImagenPerfil,
        //         lastSeen: (document.Timestamp  *  1000),//.Date
        //         br_agent: document.BrAgent,
        //         lastMessage: document.LastMessage,
        //         Unread: document.hasOwnProperty("Unread") ? document.Unread : true
        //       }
        //     if(contact.lastMessage)
        //         if(contact.lastMessage.Inbound && contact.lastMessage.Outbound)
        //             newState.push(contact);
        // }
        context.setState({ contactList: newState });
        //console.log('cambio lista');
        //console.log(newState);
      }, handleFirebaseCredentialError);
    },

    removeContacts: (db) => {
      db.ref("contacto").on("child_removed", (data) => {
        const key = data.key;

        const newContactList = context.state.contactList.filter((contact) => {
          if (contact.id === key) {
            return false;
          }
          return true;
        });

        const newMessageList = context.state.messageList.filter((message) => {
          if (message.sender === key) {
            return false;
          }
          return true;
        });

        context.setState({
          contactList: newContactList,
          messageList: newMessageList,
        });

        if (key === context.state.currentConversation) {
          window.location = "/#";
        }
      }, handleFirebaseCredentialError);
    },

    loadUsers: (db) => {
      db.ref("usuario").on("child_added", (data) => {
        const document = data.val();
        const key = data.key;
        const agente = {
          email: key,
          name: document.nombre,
          connected: document.conexiones ? true : false,
          picture: document.photoURL,
          camp: document.campana,
          emailreal: document.emailreal,
        };
        if (agente.emailreal != null) {
          agente.email = agente.emailreal.replace(/\./g, " ");
        }
        //console.log(agente)
        if (document.rol === Agente.ROLE_AGENTE_CALL_CENTER) {
          const newState = context.state.agentList.concat(agente);
          context.setState({ agentList: newState });
        }
      }, handleFirebaseCredentialError);
    },
    alterUser: (db, userId, group) => {
      if (userId) {
        const objectRef = db.ref("usuario/" + userId.replace(/\./g, " "));
        //objectRef.child('grupo').set(group);
        objectRef.update({ grupo: group });
      }
    },
    updateUsers: (db) => {
      db.ref("usuario").on("child_changed", (data) => {
        const key = data.key;
        const document = data.val();

        const newState = context.state.agentList.map((agent) => {
          if (agent.email === key) {
            agent.connected = document.conexiones ? true : false;
            return agent;
          }

          return agent;
        });

        context.setState({ agentList: newState });
      }, handleFirebaseCredentialError);
    },

    loadQuickReplies: (group) => {
      if (group) {
        const groups = group.split(",");
        groups.forEach((element) => {
          fetchWithAuth(Endpoints().GCP_QUICK_REPLIES_URL(element), {
            headers: {
              "Content-Type": "application/json",
              Authorization:
                "Bearer " +
                context.state.currentAgent.stsTokenManager.accessToken,
            },
            method: "GET",
          })
            .then((response) => {
              // Verifica que la respuesta no es null y el estado es de éxito antes de procesar el JSON
              if (response && response.ok) {
                return response.json();
              } else {
                // Maneja el caso donde la respuesta es null o el estado no es de éxito
                console.log(
                  "Respuesta nula o estado de respuesta no exitoso:",
                  response,
                );
                return null; // Retorna null o un valor predeterminado adecuado
              }
            })
            .then((replies) => {
              // Asegúrate de que replies no es null antes de intentar actualizar el estado
              if (replies) {
                const newState = context.state.quickReplies.concat(replies);
                context.setState({ quickReplies: newState });
              }
            })
            .catch((error) => {
              console.log("Error Action loadQuickReplies", error);
              context.setState({ requireLogOut: true });
            });
        });
      }
    },

    loadTypification: (group) => {
      if (group) {
        const groups = group.split(",");
        groups.forEach((element) => {
          // console.log(context.state.currentAgent.stsTokenManager.accessToken);
          fetchWithAuth(Endpoints().GCP_TYPIFICATION_URL(element), {
            headers: {
              "Content-Type": "application/json",
              Authorization:
                "Bearer " +
                context.state.currentAgent.stsTokenManager.accessToken,
            },
            method: "GET",
          })
            .then((response) => response.json())
            .catch((error) => {
              console.log("Error Action loadTypification", error);
              context.setState({ requireLogOut: true });
            })
            .then((replies) => {
              //context.setState( { quickReplies: replies })
              const newState = context.state.typification.concat(replies);

              context.setState({ typification: newState });
            });
        });
      }
    },

    createBroadcastList: (owner, broadcastListName) => {
      return fetchWithAuth(Endpoints().GCP_BROADCAST_LIST_URL(owner), {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " + context.state.currentAgent.stsTokenManager.accessToken,
        },
        method: "POST",
        body: JSON.stringify({
          broadcastList: broadcastListName,
          ownedBy: owner,
        }),
      })
        .then((response) => response.json())
        .catch((error) => {
          console.log("Error Action createBroadcastList", error);
          context.setState({ requireLogOut: true });
        });
    },

    appendBroadcastListMembers: (owner, broadcastList, members) => {
      return fetchWithAuth(
        Endpoints().GCP_BROADCAST_LIST_MEMBER_URL(owner, broadcastList.id),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer " +
              context.state.currentAgent.stsTokenManager.accessToken,
          },
          method: "POST",
          body: JSON.stringify({
            members: members,
          }),
        },
      )
        .then((response) => response.json())
        .then((members) => {
          // const newState = context.state.broadcastListList.concat(broadcastList);
          // context.setState( { broadcastListList: newState })
        })
        .catch((error) => {
          console.log("Error Action appendBroadcastListMembers", error);
          context.setState({ requireLogOut: true });
        });
    },

    removeBroadcastListMember: (owner, broadcastList, member) => {
      return fetchWithAuth(
        Endpoints().GCP_BROADCAST_LIST_MEMBER_URL(
          owner,
          `${broadcastList.id}/${member}`,
        ),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer " +
              context.state.currentAgent.stsTokenManager.accessToken,
          },
          method: "DELETE",
        },
      ).catch((error) => {
        console.log("Error Action removeBroadcastListMember", error);
        context.setState({ requireLogOut: true });
      });
    },

    removeBroadcastList: (owner, broadcastList) => {
      return fetchWithAuth(
        Endpoints().GCP_BROADCAST_LIST_MEMBER_URL(owner, `${broadcastList.id}`),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer " +
              context.state.currentAgent.stsTokenManager.accessToken,
          },
          method: "DELETE",
        },
      ).catch((error) => {
        console.log("Error Action removeBroadcastList", error);
        context.setState({ requireLogOut: true });
      });
    },

    loadBroadcastLists: (owner) => {
      return fetchWithAuth(Endpoints().GCP_BROADCAST_LIST_URL(owner), {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " + context.state.currentAgent.stsTokenManager.accessToken,
        },
        method: "GET",
      })
        .then((response) => response.json())
        .then((broadcastLists) => {
          context.setState({
            broadcastListList: broadcastLists ? broadcastLists : [],
          });
        })
        .catch((error) => {
          console.log("Error Action loadBroadcastLists", error);
          // context.setState({ requireLogOut: true });
        });
    },

    loadBroadcastListMembers: (owner, broadcastList) => {
      return fetchWithAuth(
        Endpoints().GCP_BROADCAST_LIST_MEMBER_URL(owner, broadcastList.id),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer " +
              context.state.currentAgent.stsTokenManager.accessToken,
          },
          method: "GET",
        },
      )
        .then((response) => response.json())
        .catch((error) => {
          console.log("Error Action loadBroadcastListMembers", error);
          context.setState({ requireLogOut: true });
        });
    },

    loadFullDirectory: (owner) => {
      return fetchWithAuth(Endpoints().CUSTOMER_DIRECTORY_URL(tipo, owner), {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " + context.state.currentAgent.stsTokenManager.accessToken,
        },
        method: "GET",
      })
        .then((response) => response.json())
        .then((directory) => {
          if (directory !== null && Array.isArray(directory)) {
            context.setState({ customerDirectory: directory });
          } else {
            context.setState({ customerDirectory: [] }); // Asigna un array vacío si directory es null
            console.log(
              "Falla al consultar la cartera, el directorio esta vacio",
            );
          }
        })
        .catch((error) => {
          console.log("COULD NOT LOAD CLIENT DIRECTORY", error);
          // context.setState({ requireLogOut: true });
        });
    },

    loadParameter: (param) => {
      return fetchWithAuth(Endpoints().GCP_PARAMETERS(param), {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " + context.state.currentAgent.stsTokenManager.accessToken,
        },
        method: "GET",
      })
        .then((response) => response.json())
        .then((parameter) => {
          const newState = {
            ...context.state.parameter,
            [parameter.label]: parameter.value,
          };
          context.setState({ parameter: newState });
        })
        .catch((error) => {
          console.log("COULD NOT LOAD PARAMETER", error);
          context.setState({ requireLogOut: true });
        });
    },

    loadAttentionsDays: (group) => {
      if (group.toLowerCase().includes("cobranza")) {
        group = "cobranza";
      } else {
        group = "*";
      }

      return fetchWithAuth(Endpoints().GCP_ATTENTION_DAYS(group), {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " + context.state.currentAgent.stsTokenManager.accessToken,
        },
        method: "GET",
      })
        .then((response) => response.json())
        .then((data) => {
          const newState = context.state.attentionDays.concat(data);
          context.setState({ attentionDays: newState });
        })
        .catch((error) => {
          console.log("COULD NOT LOAD ATTENTION DAYS", error);
          context.setState({ requireLogOut: true });
        });
    },
    loadPermissions: async (grupo) => {
      try {
        const response = await fetchWithAuth(Endpoints().GCP_GROUP_URL(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer " +
              context.state.currentAgent.stsTokenManager.accessToken,
          },
        });
        const data = await response.json();

        const permissionsForGroup =
          data.groups.find((g) => g.groupId === grupo)?.permissions || [];
        return permissionsForGroup;
      } catch (err) {
        console.log("COULD NOT LOAD PERMISSIONS", err);
        context.setState({ requireLogOut: true });
        return [];
      }
    },
  };
}
